// Shared with BalanceForecastChart.tsx and Subscription.tsx — both port
// prototype date math (`MONTHS_FULL` in usage-billing.html) and shouldn't
// each keep their own copy.
export const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Format a `YYYY-MM` key as a month-and-year label in the active UI language
 * — the Usage page's period picker.
 *
 * `Intl` rather than extending MONTHS_FULL above (which is hardcoded English,
 * inherited from the prototype's own date math): the twelve month names
 * already exist in every locale the browser ships, so a `t()` table would be
 * sixteen hand-maintained copies of data the platform has correct — and would
 * still get the *order* of month and year wrong for locales that lead with
 * the year. Nothing is hardcoded English here, which is what the i18n
 * invariant protects.
 *
 * Parsed by field rather than `new Date("2026-09")`, which the language spec
 * reads as UTC midnight — one timezone step west of UTC and that renders as
 * the *previous* month. */
export function formatMonth(month: string, language: string): string {
  const [year, monthNumber] = month.split('-').map(Number)
  return new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(
    new Date(year, monthNumber - 1, 1)
  )
}

/** `YYYY-MM-DD` as a local calendar date, parsed by field (see `formatMonth`
 * on why `new Date("2026-09-30")` would be a day early west of UTC). */
export function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** The locale dates are written in. English is the prototype's own
 * day-month-year ("30 September 2026", `fmtDate`) — British order, which bare
 * `en` (US: "September 30, 2026") is not; every other language keeps its own. */
export function dateLocale(language: string): string {
  return language === 'en' ? 'en-GB' : language
}

/** A calendar date the prototype prints as "30 September 2026" (`fmtDate`),
 * in the active UI language's own order and month names (MS-820). */
export function formatLongDate(iso: string, language: string): string {
  return new Intl.DateTimeFormat(dateLocale(language), { day: 'numeric', month: 'long', year: 'numeric' }).format(
    parseIsoDate(iso)
  )
}

/** A purchase instant in the viewer's own locale and timezone — the
 * prototype's "01 July 2026 09:12" Order-history Date & time. */
export function formatDateTime(isoInstant: string, language: string): string {
  return new Intl.DateTimeFormat(dateLocale(language), {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(isoInstant))
}

/** The chart's month labels: the prototype's `Oct '26` short form and
 * `October 2026` tooltip heading, localised. The short form is the
 * language's short month name plus the prototype's `'26` in every language:
 * Intl's own short month-and-year is up to twice as wide ("окт. 26 г.",
 * "out. de 26") for 12–13 ticks under narrow bars, and in Thai gives the
 * two-digit Buddhist year ("ต.ค. 69") beside Gregorian data. */
export function monthLabels(d: Date, language: string): { short: string; label: string } {
  return {
    short: `${new Intl.DateTimeFormat(language, { month: 'short' }).format(d)} '${String(d.getFullYear()).slice(2)}`,
    label: new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(d),
  }
}

/** A grant's "Added" date (Settings › Access, MS-997): the prototype's
 * "Jun 29, 2026", in the active UI language's own order and short month name
 * ("29 Jun 2026" in English, as every date here; ADR §57, Q9). An instant, so
 * it is shown in the viewer's own timezone. */
export function formatShortDate(isoInstant: string, language: string): string {
  return new Intl.DateTimeFormat(dateLocale(language), { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(isoInstant)
  )
}
